import { useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { TransitionGroup } from 'react-transition-group';
import { Typography, Fade, makeStyles } from '@material-ui/core';
import UserCount from '../../userCount';
import ActionButton from '../../actionButton';
import RichTextPreview from '../../richTextPreview';
import StatusIndicator from '../../statusIndicator';
import DotsMenu from '../../dotsMenu';
import Tag from '../../tag';
import TextBoxWithTooltip from '../../textBoxWithTooltip';
import UserLink from '../../userLink';
import TasksIconTooltip from '../../tasksIconTooltip';
import CoursesIconTooltip from '../../coursesIconTooltip';
import RequestedIconTooltip from '../../requestedIconTooltip';
import ProposedStatus from '../../proposedStatus';
import { ellipsis } from '../../../../constants/helperCssRules';
import { isArrayEmpty } from '../../../../utility/helpers';
import { getContentLength } from '../../../../utility/uiUtils';
import { prepareActionPlansDotsMenuItems } from '../../../../utility/actionPlans';
import { formatDate } from '../../../../utility/dateUtils';
import { ACTION_BUTTON_TYPES } from '../../actionButton/config';
import { DAY_MONTH_FORMAT, YEAR_FORMAT } from '../config';
import http from '../../../../utility/http';
import { api_action_plans_with_view_permission } from '../../../../constants/apiRoutes';
import { ReactComponent as PreviewUserIcon } from '../../../../assets/icons/remove_red_eye.svg';

const useStyles = makeStyles(
  ({ palette: { primary }, spacing, breakpoints }) => ({
    root: {
      backgroundColor: primary.bluish8,
      boxSizing: 'border-box',
      border: '1px solid transparent',
      borderRadius: 4,
      display: 'grid',
      gridTemplateColumns: `minmax(0, 1fr) 230px`,
      width: '100%',
      transition: 'border .2s ease',
    },
    clickableCard: {
      cursor: 'pointer',
      '&:hover': {
        border: `1px solid ${primary.blue1}`,
      },
    },
    content: {
      boxSizing: 'border-box',
      padding: spacing(3),
      width: '100%',
      [breakpoints.up('xLg')]: {
        padding: spacing(6),
      },
    },
    descriptionPreviewButton: {
      marginLeft: spacing(1.5),
    },
    info: {
      backgroundColor: primary.bluish9,
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      padding: spacing(6, 5),
    },
    statusWrapper: {
      display: 'flex',
      alignItems: 'center',
    },
    sectionWrapper: {
      marginBottom: spacing(4),
    },
    labelSpacing: {
      marginBottom: spacing(1.5),
    },
    label: {
      color: primary.bluish3,
    },
    day: {
      marginBottom: spacing(1),
    },
    dotsMenu: {
      position: 'absolute',
      right: 16,
      top: 16,
      height: 20,
      width: 'auto',
      [breakpoints.up('xLg')]: {
        right: 24,
        top: 24,
      },
    },
    userCount: {
      position: 'absolute',
      right: 22,
      bottom: 22,
    },
    descriptionLabelWrapper: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: spacing(1),
      lineHeight: '18px',
    },
    descriptionText: {
      ...ellipsis(),
      display: '-webkit-box',
      '-webkit-line-clamp': 3,
      '-webkit-box-orient': 'vertical',
      whiteSpace: 'normal',
    },
    tagsWrapper: {
      boxSizing: 'border-box',
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      minHeight: 28,
      width: '100%',
    },
    tagsWrapperAll: {
      marginBottom: spacing(2),
    },
    tagWrapper: {
      margin: spacing(0, 2, 2, 0),
    },
    tagsWrapperMini: {
      display: 'flex',
      alignItems: 'center',
      minHeight: 28,
    },
    tagsSectionSpacing: {
      marginBottom: spacing(4),
    },
    autoSpacing: {
      marginTop: 'auto',
      maxWidth: 'calc(100% - 26px)',
    },
    createdByHighlightedLabel: {
      fontFamily: 'ProximaNova-Bold',
    },
    iconTooltipWrapper: {
      display: 'flex',
      alignItems: 'center',
    },
    iconSpacing: {
      marginLeft: spacing(2),
    },
    pendingProposalWrapper: {
      border: `1px solid ${primary.bluish5}`,
      borderRadius: 8,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: spacing(2, 3),
    },
    actionButtons: {
      display: 'flex',
      justifyContent: 'flex-end',
    },
    actionButton: {
      marginLeft: spacing(1.5),
    },
    proposed: {
      marginLeft: spacing(1.5),
    },
    accessible: {
      cursor: 'pointer',
    },
  })
);

const ActionPlanCard = ({
  className = null,
  translations,
  actionPlan,
  statuses,
  isCreatedByMe = false,
  isCreatorAccessible = false,
  isCreatedForCurrentUser,
  canViewCourses,
  isDisabled = false,
  canAcceptProposal = false,
  canView = false,
  canEdit = false,
  canDelete = false,
  onCardClick = () => {},
  canAccessUserProfileCheck,
  onDelete = () => {},
  onEdit = () => {},
  onManageTasks = () => {},
  onPreviewDescription = () => {},
  onDismissProposal = () => {},
  goToUserProfilePage,
}) => {
  const classes = useStyles();
  const {
    title,
    due_date,
    description,
    tags,
    created_on,
    creator,
    tasks,
    progress,
    is_proposed,
    is_pending_proposal,
    is_finished,
    courses,
  } = actionPlan;
  const { ACCEPT, CLOSE, PREVIEW_DESCRIPTION } = ACTION_BUTTON_TYPES;

  const hasTasks = !isArrayEmpty(tasks);
  const hasCourses = !isArrayEmpty(courses);
  const hasDescription = description && getContentLength(description) > 0;
  const isPendingProposal = is_proposed && is_pending_proposal;
  const canManageOwnPendingProposal =
    isPendingProposal && isCreatedForCurrentUser;
  const hasDotsMenu = canEdit || canDelete || isCreatedForCurrentUser;

  const handleCardClick = useCallback(() => {
    if (canView) {
      onCardClick(actionPlan);
    }
  }, [actionPlan, canView, onCardClick]);

  const handleEdit = useCallback(
    () => onEdit(actionPlan),
    [actionPlan, onEdit]
  );

  const handleAcceptProposal = useCallback(
    e => {
      e.stopPropagation();
      handleEdit();
    },
    [handleEdit]
  );

  const handleDelete = useCallback(() => {
    onDelete(actionPlan);
  }, [actionPlan, onDelete]);

  const handleDismissProposal = useCallback(
    e => {
      e?.stopPropagation();
      onDismissProposal(actionPlan);
    },
    [actionPlan, onDismissProposal]
  );

  const handleDismissProposalFromMenu = useCallback(
    aPlan => {
      onDismissProposal(aPlan);
    },
    [onDismissProposal]
  );

  const handlePreviewDescription = useCallback(
    e => {
      e.stopPropagation();

      onPreviewDescription(actionPlan);
    },
    [actionPlan, onPreviewDescription]
  );

  const renderStatusLabel = () => {
    const currentStatus = statuses.find(status => status.value === is_finished);

    return (
      <div className={classes.statusWrapper}>
        <StatusIndicator status={currentStatus} />
        {is_proposed && (
          <RequestedIconTooltip
            className={classes.proposed}
            labels={translations.requested}
          />
        )}
      </div>
    );
  };

  const renderAllTags = () => {
    return !isArrayEmpty(tags) ? (
      <TransitionGroup
        className={classNames(classes.tagsWrapper, classes.tagsWrapperAll)}
      >
        {tags.map(tag => (
          <Fade key={`tag_item_${tag.id}`} in>
            <div className={classes.tagWrapper}>
              <Tag tag={tag} color={tag.category.color} isSelected />
            </div>
          </Fade>
        ))}
      </TransitionGroup>
    ) : (
      <Typography
        className={classNames(classes.tagsWrapperMini, classes.tagsWrapperAll)}
      >
        -
      </Typography>
    );
  };

  const getUsersWithViewPermission = useCallback(
    () => http.get(api_action_plans_with_view_permission(actionPlan.id)),
    [actionPlan.id]
  );

  return (
    <div
      className={classNames(
        classes.root,
        { [classes.clickableCard]: canView },
        className
      )}
      onClick={handleCardClick}
    >
      <div className={classes.content}>
        {!isDisabled && isPendingProposal && canAcceptProposal && (
          <div className={classes.sectionWrapper}>
            <div className={classes.pendingProposalWrapper}>
              <Typography variant="subtitle2">
                {translations.proposeAccept}
              </Typography>
              <div className={classes.actionButtons}>
                <ActionButton
                  className={classes.actionButton}
                  type={ACCEPT}
                  tooltipText={translations.actionButtons[ACCEPT.toLowerCase()]}
                  onClickHandler={handleAcceptProposal}
                  isSquared
                />
                <ActionButton
                  className={classes.actionButton}
                  type={CLOSE}
                  tooltipText={translations.actionButtons.dismiss}
                  onClickHandler={handleDismissProposal}
                  isSquared
                />
              </div>
            </div>
          </div>
        )}
        <div className={classes.sectionWrapper}>
          <Typography
            className={classNames(classes.label, classes.labelSpacing)}
            variant="body2"
          >
            {translations.title}
          </Typography>
          <TextBoxWithTooltip variant="subtitle2" text={title} />
        </div>
        <div className={classes.sectionWrapper}>
          <div className={classes.descriptionLabelWrapper}>
            <Typography className={classes.label} variant="body2">
              {translations.description}
            </Typography>
            {hasDescription && (
              <ActionButton
                className={classes.descriptionPreviewButton}
                type={PREVIEW_DESCRIPTION}
                tooltipText={translations.preview}
                onClickHandler={handlePreviewDescription}
              />
            )}
          </div>
          {hasDescription ? (
            <RichTextPreview
              className={classes.descriptionText}
              text={description}
              isEllipsis
            />
          ) : (
            <Typography variant="body2">-</Typography>
          )}
        </div>
        <div>
          <Typography
            className={classNames(classes.label, classes.labelSpacing)}
            variant="body2"
          >
            {translations.tags}
          </Typography>
          {renderAllTags()}
        </div>
      </div>
      <div className={classes.info}>
        <div className={classes.sectionWrapper}>
          {isPendingProposal ? (
            <ProposedStatus proposedLabel={translations.proposed} />
          ) : (
            renderStatusLabel()
          )}
        </div>
        {!isPendingProposal && (
          <div className={classes.sectionWrapper}>
            <Typography className={classes.labelSpacing} variant="body2">
              {translations.completeness}
            </Typography>
            <Typography variant="subtitle1">{`${progress}%`}</Typography>
          </div>
        )}
        <div className={classes.sectionWrapper}>
          <Typography className={classes.labelSpacing} variant="body2">
            {translations.dueDate}
          </Typography>
          <Typography variant="subtitle1" className={classes.day}>
            {formatDate(due_date, DAY_MONTH_FORMAT)}
          </Typography>
          <Typography variant="overline">
            {formatDate(due_date, YEAR_FORMAT)}
          </Typography>
        </div>
        <div className={classes.sectionWrapper}>
          <Typography className={classes.labelSpacing} variant="body2">
            {isPendingProposal
              ? translations.proposedOn
              : translations.createdOn}
          </Typography>
          <Typography variant="subtitle1" className={classes.day}>
            {formatDate(created_on, DAY_MONTH_FORMAT)}
          </Typography>
          <Typography variant="overline">
            {formatDate(created_on, YEAR_FORMAT)}
          </Typography>
        </div>
        <div className={classes.iconTooltipWrapper}>
          <CoursesIconTooltip
            className={classes.sectionWrapper}
            text={translations.coursesIconTooltip}
            isVisible={canViewCourses && hasCourses}
          />
          <TasksIconTooltip
            className={classNames(classes.sectionWrapper, {
              [classes.iconSpacing]: hasCourses,
            })}
            iconClass={classes.accessible}
            text={translations.tasksIconTooltip}
            isVisible={hasTasks}
            numberOfTasks={tasks?.length}
          />
        </div>
        {!isPendingProposal && (
          <UserLink
            className={classes.autoSpacing}
            label={translations.createdBy}
            labelClass={classNames({
              [classes.createdByHighlightedLabel]: isCreatedByMe,
            })}
            meLabel={translations.createdByMe}
            user={creator}
            isUserAccessible={isCreatorAccessible}
            isCurrentUser={isCreatedByMe}
          />
        )}
        <UserCount
          className={classes.userCount}
          icon={PreviewUserIcon}
          tooltipTitle={translations.usersListTitle}
          tooltipLabel={translations?.usersListTooltip}
          hasUserCount={false}
          canAccessUserProfileCheck={canAccessUserProfileCheck}
          getUsers={getUsersWithViewPermission}
          goToUserProfile={goToUserProfilePage}
          isPreviewIcon
        />
        {hasDotsMenu && (
          <DotsMenu
            className={classes.dotsMenu}
            menuItems={prepareActionPlansDotsMenuItems(
              translations.menuItemLabels,
              actionPlan,
              canEdit || canManageOwnPendingProposal,
              canDelete || canManageOwnPendingProposal,
              isDisabled,
              isPendingProposal,
              isCreatedForCurrentUser,
              handleEdit,
              handleDelete,
              onManageTasks,
              handleDismissProposalFromMenu
            )}
          />
        )}
      </div>
    </div>
  );
};

ActionPlanCard.propTypes = {
  className: PropTypes.string,
  goToUserProfilePage: PropTypes.func.isRequired,
  translations: PropTypes.object.isRequired,
  actionPlan: PropTypes.shape({}).isRequired,
  statuses: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  isCreatedByMe: PropTypes.bool,
  isCreatorAccessible: PropTypes.bool,
  isCreatedForCurrentUser: PropTypes.bool.isRequired,
  canAcceptProposal: PropTypes.bool,
  canViewCourses: PropTypes.bool.isRequired,
  isDisabled: PropTypes.bool,
  canView: PropTypes.bool,
  canEdit: PropTypes.bool,
  canDelete: PropTypes.bool,
  onCardClick: PropTypes.func,
  onDelete: PropTypes.func,
  onEdit: PropTypes.func,
  onManageTasks: PropTypes.func,
  onPreviewDescription: PropTypes.func,
  onDismissProposal: PropTypes.func,
  canAccessUserProfileCheck: PropTypes.func.isRequired,
};

export default ActionPlanCard;
